import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/balanced.css';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [passwordRecoverySent, setPasswordRecoverySent] = useState(false);
  const [passwordRecoveryLoading, setPasswordRecoveryLoading] = useState(false);
  const { signUp, resendConfirmationEmail, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const result = await signUp(email, password, fullName);

      if (result.emailAlreadyExists) {
        // Email is already registered — show dedicated UI
        setEmailAlreadyExists(true);
      } else if (result.confirmationRequired) {
        setEmailSent(true);
        setSuccess('Success! Please check your email to confirm your account before signing in.');
      } else {
        // Rare case: auto-login without confirmation
        setSuccess('Account created successfully! Redirecting...');
        setTimeout(() => window.location.href = '/dashboard', 2000);
      }
    } catch (err) {
      // Handle specific error cases
      const errorMessage = err.message || '';
      if (errorMessage.includes('body stream already read') || errorMessage.includes('429')) {
        setError('Too many signup attempts. Please wait a few minutes and try again.');
      } else if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        setEmailAlreadyExists(true);
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordRecovery = async () => {
    setPasswordRecoveryLoading(true);
    setError('');
    try {
      await resetPassword(email);
      setPasswordRecoverySent(true);
    } catch (err) {
      const errorMessage = err.message || '';
      if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError(err.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setPasswordRecoveryLoading(false);
    }
  };

  const handleResend = async () => {
    setResendSuccess('');
    setError('');
    setResendLoading(true);
    try {
      await resendConfirmationEmail(email);
      setResendSuccess('Confirmation email resent! Please check your inbox.');
    } catch (err) {
      const errorMessage = err.message || '';
      if (errorMessage.includes('rate limit') || errorMessage.includes('429')) {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError(err.message || 'Failed to resend email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (emailAlreadyExists) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--light-gray)' }}>
        <div style={{ width: '100%', maxWidth: '500px', padding: '2rem' }}>
          <div className="card-emphasis">
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#fef3c7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <span style={{ fontSize: '2.5rem' }}>👤</span>
              </div>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem' }}>Email Already Registered</h1>
              <p style={{ color: 'var(--gray)', lineHeight: '1.6' }}>
                An account with <strong>{email}</strong> already exists.
              </p>
            </div>

            <div style={{ background: 'var(--light-gray)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', marginBottom: '0.5rem' }}>
                <strong>What would you like to do?</strong>
              </p>
              <ul style={{ fontSize: '0.875rem', color: 'var(--charcoal)', paddingLeft: '1.25rem', marginBottom: 0 }}>
                <li>Sign in with your existing account</li>
                <li>Reset your password if you've forgotten it</li>
              </ul>
            </div>

            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }} data-testid="signup-error">
                {error}
              </div>
            )}

            {passwordRecoverySent ? (
              <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }} data-testid="recovery-sent-msg">
                ✅ Password reset email sent to <strong>{email}</strong>. Check your inbox.
              </div>
            ) : null}

            <Link
              to={`/login?email=${encodeURIComponent(email)}`}
              className="btn btn-primary"
              style={{ width: '100%', textAlign: 'center', display: 'block', marginBottom: '0.75rem' }}
              data-testid="go-to-login-btn"
            >
              Sign In to Existing Account
            </Link>

            {!passwordRecoverySent && (
              <button
                onClick={handlePasswordRecovery}
                disabled={passwordRecoveryLoading}
                className="btn"
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--gold)',
                  color: 'var(--gold)',
                  fontWeight: 600,
                  padding: '0.625rem 1rem',
                  borderRadius: '0.375rem',
                  cursor: passwordRecoveryLoading ? 'not-allowed' : 'pointer',
                  opacity: passwordRecoveryLoading ? 0.6 : 1,
                  marginBottom: '0.75rem'
                }}
                data-testid="recover-password-btn"
              >
                {passwordRecoveryLoading ? 'Sending reset email...' : 'Forgot Password? Send Reset Link'}
              </button>
            )}

            <p style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--gray)' }}>
              Used a different email?{' '}
              <button
                onClick={() => {
                  setEmailAlreadyExists(false);
                  setError('');
                  setPasswordRecoverySent(false);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--gold)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                data-testid="try-different-email-btn"
              >
                Try a different email
              </button>
            </p>
          </div>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Link to="/" style={{ color: 'var(--gray)', fontSize: '0.875rem', textDecoration: 'none' }}>
              ← Back to website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (emailSent) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--light-gray)' }}>
        <div style={{ width: '100%', maxWidth: '500px', padding: '2rem' }}>
          <div className="card-emphasis">
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#d1fae5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <span style={{ fontSize: '2.5rem' }}>📧</span>
              </div>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem' }}>Check Your Email</h1>
              <p style={{ color: 'var(--gray)', lineHeight: '1.6' }}>
                We've sent a confirmation link to <strong>{email}</strong>
              </p>
            </div>

            <div style={{ background: 'var(--light-gray)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', marginBottom: '0.5rem' }}>
                <strong>What's next?</strong>
              </p>
              <ol style={{ fontSize: '0.875rem', color: 'var(--charcoal)', paddingLeft: '1.25rem', marginBottom: 0 }}>
                <li>Check your inbox (and spam folder)</li>
                <li>Click the confirmation link</li>
                <li>Return here to sign in</li>
              </ol>
            </div>

            {/* Localhost testing help */}
            {window.location.hostname === 'localhost' && (
              <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
                  <strong>🛠️ Local Testing Tip:</strong>
                </p>
                <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
                  For local development, you have 2 options:
                </p>
                <ol style={{ fontSize: '0.875rem', color: '#92400e', paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li>Check your email and click the real confirmation link</li>
                  <li>Or go to Supabase Dashboard → Authentication → Users, find your user, and click "Confirm email"</li>
                </ol>
                <p style={{ fontSize: '0.8rem', color: '#92400e', marginBottom: 0 }}>
                  To skip confirmation entirely, disable "Confirm email" in Supabase Dashboard → Authentication → Providers → Email
                </p>
              </div>
            )}

            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            {resendSuccess && (
              <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
                {resendSuccess}
              </div>
            )}

            <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }} data-testid="go-to-signin-btn">
              Go to Sign In
            </Link>

            <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: 'var(--gray)' }}>
              Didn't receive the email?{' '}
              <button
                onClick={handleResend}
                disabled={resendLoading}
                style={{ background: 'none', border: 'none', color: 'var(--gold)', fontWeight: 600, cursor: resendLoading ? 'not-allowed' : 'pointer', textDecoration: 'underline', opacity: resendLoading ? 0.6 : 1 }}
                data-testid="resend-email-btn"
              >
                {resendLoading ? 'Sending...' : 'Resend'}
              </button>
            </p>
          </div>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Link to="/" style={{ color: 'var(--gray)', fontSize: '0.875rem', textDecoration: 'none' }}>
              ← Back to website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--light-gray)' }}>
      <div style={{ width: '100%', maxWidth: '400px', padding: '2rem' }}>
        <div className="card-emphasis">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img 
              src="/fNew_logo_2.png" 
              alt="IDNTY Logo" 
              style={{ height: '70px', width: 'auto', marginBottom: '0.5rem' }}
            />
            <p style={{ color: 'var(--gray)' }}>Create your account</p>
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }} data-testid="signup-error">
              {error}
            </div>
          )}

          {success && (
            <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }} data-testid="signup-success">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="signup-form">
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="form-input"
                placeholder="John Doe"
                data-testid="fullname-input"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                placeholder="you@company.com"
                data-testid="email-input"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="form-input"
                placeholder="••••••••"
                minLength={6}
                data-testid="password-input"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '0.25rem' }}>
                Must be at least 6 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '1rem' }}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--gray)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--gold)', fontWeight: 600, textDecoration: 'none' }} data-testid="signin-link">
                Sign in
              </Link>
            </p>
          </form>
        </div>

        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--gray)', fontSize: '0.875rem', textDecoration: 'none' }}>
            ← Back to website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
